import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, Send, Loader2, X, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Self-service appeal modal.
 *
 * Shown when a user is blocked by the Account Health hard gate (403
 * `account_removed`) or proactively from the Account Health tab. Fetches the
 * user's own health, pre-fills a message with the strike history and posts
 * it as a support ticket flagged `[APPEAL]` so admins can filter.
 *
 * Backend: reuses the existing POST /api/support/contact-admin endpoint so
 * the appeal shows up in the admin Support inbox just like any other
 * customer message.
 */
const AccountAppealModal = ({ isOpen, onClose, initialHealth = null }) => {
  const [health, setHealth] = useState(initialHealth);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setDone(false);
    if (health) return;
    const token =
      localStorage.getItem('user_token') || localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API_BASE}/account-health/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        // Prefer whichever scope is actually flagged.
        const g = res.data?.guest;
        const h = res.data?.host;
        const worst = [g, h]
          .filter(Boolean)
          .sort(
            (a, b) =>
              ['healthy', 'warning', 'suspended', 'removed'].indexOf(b.tier) -
              ['healthy', 'warning', 'suspended', 'removed'].indexOf(a.tier),
          )[0];
        setHealth(worst || g || h);
      })
      .catch(() => setHealth(null));
  }, [isOpen, health]);

  const submit = async () => {
    const token =
      localStorage.getItem('user_token') || localStorage.getItem('token');
    if (!token) {
      toast({
        title: 'Please sign in',
        description: 'You need an account to submit an appeal.',
        variant: 'destructive',
      });
      return;
    }
    setSubmitting(true);
    const strikeLines = (health?.strikes || [])
      .slice(0, 10)
      .map(
        (s, i) =>
          `  ${i + 1}. ${s.rating}★ · ${s.created_at?.slice(0, 10)} · ${
            s.property_title || 'Property'
          }${s.comment ? ` — "${s.comment.slice(0, 160)}"` : ''}`,
      )
      .join('\n');
    const body = [
      '[APPEAL] Account Health Restriction',
      '',
      `Current tier: ${health?.tier?.toUpperCase() || 'UNKNOWN'}`,
      `Strike count (90d): ${health?.strike_count ?? 0}`,
      '',
      'Recent strikes:',
      strikeLines || '  (none returned by account-health endpoint)',
      '',
      '---',
      'Guest explanation:',
      reason.trim() || '(no message provided)',
    ].join('\n');

    try {
      await axios.post(
        `${API_BASE}/support/contact-admin`,
        { message: body },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDone(true);
      toast({
        title: 'Appeal submitted',
        description:
          'Our support team will review your account and respond by email. Thank you for being patient.',
      });
    } catch (err) {
      toast({
        title: 'Could not submit appeal',
        description:
          err?.response?.data?.detail || 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="account-appeal-modal"
    >
      <Card className="w-full max-w-lg flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-gray-900">Appeal account restriction</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            data-testid="account-appeal-close"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto max-h-[60vh]">
          {done ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <Send className="w-5 h-5 text-emerald-700" />
              </div>
              <p className="font-semibold text-gray-900 mb-1">Appeal received</p>
              <p className="text-sm text-gray-600">
                An admin will review your account standing and respond by email
                within 1–2 business days.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-700">
                We'll send your appeal to the support team along with a summary of
                your recent reviews. Admins can forgive individual strikes or
                restore your account.
              </p>

              {health && health.strikes && health.strikes.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                    Strikes on your account (last {health.rolling_window_days || 90}d)
                  </p>
                  <ul className="space-y-1.5">
                    {health.strikes.slice(0, 5).map((s) => (
                      <li key={s.id} className="text-xs text-gray-700 flex items-start gap-2">
                        <span className="inline-flex items-center gap-0.5 text-amber-500 mt-0.5">
                          {Array.from({ length: Math.max(1, s.rating || 1) }).map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-current" />
                          ))}
                        </span>
                        <span className="flex-1">
                          <span className="font-semibold">{s.property_title || 'Stay'}</span>
                          {s.comment && <span className="italic text-gray-600"> · “{s.comment.slice(0, 80)}”</span>}
                          <span className="text-gray-400 ml-1">({s.created_at?.slice(0, 10)})</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your explanation (optional but recommended)
                </label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, 2000))}
                  placeholder="Tell us what happened on these stays — we read every appeal."
                  rows={5}
                  data-testid="account-appeal-textarea"
                  className="resize-none"
                />
                <p className="mt-1 text-[11px] text-gray-400 text-right">
                  {reason.length}/2000
                </p>
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="account-appeal-cancel">
            {done ? 'Close' : 'Cancel'}
          </Button>
          {!done && (
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="account-appeal-submit"
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit appeal
                </>
              )}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default AccountAppealModal;
